import { emitHintSignal } from '@flobi/asset-library/hints';
import { useEffect, useMemo, useRef, useState } from 'react';
import { BLEND_MODES, CONTAINER_TYPES, effectName, ROOT_ID, toSvg, TOP_LEVEL_ONLY_TYPES, type Effect, type ShaderDef, type LayerBlendMode, type LengthUnit, type NodeRecord, type Scene } from '@flobi/artwork-core';
import { Icon, type IconName } from './icons';
import { Hint, IconButton, NumberField, PopUpButton, Slider, Switch } from '../../ui/controls';
import { Menu, type MenuEntry } from '../../ui/menu';
import { layerMoveTarget, pickInList, pickModeOf, type PickMode } from './focusedLayers';
import { Popover } from '../../ui/popover';
import { tip } from '../../ui/tooltip';
import { EffectsList } from '../shell/Effects';
import { listedEffects } from '../shell/effectList';

/**
 * Layer navigation with shared drag, swipe, blend and effects controls.
 * Focused mode shows one artboard, collapsible groups and a per-layer action menu.
 */

export interface LayerAction {
  readonly id: string;
  readonly label: string;
  readonly icon: IconName;
  readonly danger?: boolean;
  run(id: string): void;
}

/**
 * How far a row slides aside, which is exactly how wide the actions behind it are: a row
 * that slid further would hide its own name for nothing.
 */
const SWIPE_OPEN = 96;
/** And how far it must be dragged down before the drag is a reorder rather than a tap. */
const DRAG_START = 5;
/**
 * How deep the indent goes. The list goes all the way down (layers-F8); only the step in
 * from the edge stops, because past six levels an indent is spending the name's room to
 * say something the order of the rows already says.
 */
const INDENT_LEVELS = 6;
/**
 * The top of a container's row that still means "above it". Below that, the row means
 * "into it": the next row down is the container's own top layer, so that is where the
 * layer visibly lands.
 */
const ABOVE_CONTAINER = 0.3;

/** Where a dragged row would land: next to a row, or inside it. */
type Landing = { id: string; where: 'above' | 'below' | 'into' };

export function LayersPanel({
  scene,
  selection,
  version,
  onSelect,
  onSelectIds,
  onGroup,
  onUngroup,
  onUpdate,
  onMove,
  actions,
  onAddToChat,
  onEditText,
  units,
  onEffect,
  onRemoveEffect,
  onMoveEffect,
  openEffects,
  getShader,
  parentId = ROOT_ID,
  focused = false,
  documentTree = false,
  compact = false,
}: {
  scene: Scene;
  parentId?: string;
  focused?: boolean;
  /** The whole infinite board, with collapsible artboards and loose layers. */
  documentTree?: boolean;
  /** The split navigator's 28px rows, with small type icons and direct lock controls. */
  compact?: boolean;
  selection: string[];
  /** Bumped whenever the page changes, so the list re-reads the scene. */
  version: number;
  onSelect: (id: string) => void;
  /** Several layers picked at once, by Shift (a run) or ⌘/Ctrl (one more or fewer). Without it every click picks one. */
  onSelectIds?: (ids: string[]) => void;
  /** "Group" in a row's menu while several layers are picked. Left out without it. */
  onGroup?: (id: string) => void;
  /** "Ungroup" in a group row's menu. Left out without it. */
  onUngroup?: (id: string) => void;
  onUpdate: (id: string, props: Record<string, unknown>) => void;
  /** Put a layer next to another one, in that one's parent. */
  onMove: (id: string, target: { parentId: string; before?: string; after?: string }) => void;
  /** Lock, duplicate, delete: what a row swiped aside offers. */
  actions: readonly LayerAction[];
  /** "Add to chat" in a row's menu: the layer pinned to the agent's next message. Left out without it. */
  onAddToChat?: (id: string) => void;
  /** "Edit text" in a text row's menu: the way into a text the canvas cannot reach, such as one cutting out a picture. */
  onEditText?: (id: string) => void;
  /** The unit the document is thought in, for the lengths an effect is measured in. */
  units: LengthUnit;
  /** Editing one layer's effects, from the FX beside its name. */
  onEffect: (id: string, index: number, props: Record<string, unknown>) => void;
  onRemoveEffect: (id: string, index: number) => void;
  onMoveEffect: (id: string, index: number, to: number) => void;
  /** Which layer's effects to have open when the list appears, if any. */
  openEffects?: string | null;
  getShader?: (id: string) => ShaderDef | null;
}) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(documentTree ? scene.childrenOf(ROOT_ID).filter(id => scene.get(id)?.type === 'artboard') : []));
  const seenBoards = useRef(new Set<string>());
  useEffect(() => {
    if (!documentTree) return;
    // Page contents can arrive after the panel mounts. Open new artboards once,
    // while respecting any artboard the person has since collapsed.
    const added = scene.childrenOf(ROOT_ID).filter(id => scene.get(id)?.type === 'artboard' && !seenBoards.current.has(id));
    if (!added.length) return;
    added.forEach(id => seenBoards.current.add(id));
    setExpanded(current => new Set([...current, ...added]));
  }, [documentTree, scene, version]);
  const [rowMenu, setRowMenu] = useState<{ id: string; anchor: HTMLElement } | null>(null);
  const [swiped, setSwiped] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [blending, setBlending] = useState<string | null>(null);
  const [blendAnchor, setBlendAnchor] = useState<HTMLElement | null>(null);
  const [effectsAnchor, setEffectsAnchor] = useState<HTMLElement | null>(null);
  const [showing, setShowing] = useState<string | null>(openEffects ?? null);
  const [dragging, setDragging] = useState<{ id: string; y: number } | null>(null);
  const [drop, setDrop] = useState<Landing | null>(null);
  const list = useRef<HTMLUListElement>(null);
  /** Where a Shift run starts: the row the last plain or ⌘ click landed on. */
  const anchor = useRef<string | null>(null);

  useEffect(() => {
    if (!focused) return;
    setExpanded(current => {
      const next = new Set(current);
      for (const id of selection) for (const ancestor of scene.ancestors(id)) next.add(ancestor);
      return next.size === current.size ? current : next;
    });
  }, [scene, selection, focused]);

  const rows = useMemo(() => {
    const out: { id: string; node: NodeRecord; depth: number }[] = [];
    const walk = (parent: string, depth: number) => {
      // Top of the stack first, as every layer list reads.
      for (const id of [...scene.childrenOf(parent)].reverse()) {
        const node = scene.get(id);
        if (!node || (focused && !documentTree && parent === ROOT_ID && node.type === 'artboard')) continue;
        out.push({ id, node, depth });
        // Every level (layers-F8). This stopped at four, and a boolean, a blend and a
        // cloner count as levels too, so the innermost layers of an ordinary drawing had
        // no row: nothing to pick, rename, hide or move them by.
        if (!focused || expanded.has(id)) walk(id, depth + 1);
      }
    };
    walk(parentId, 0);
    return out;
    // The scene is mutable; the version is what says it changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, version, parentId, focused, documentTree, expanded, selection]);

  /**
   * Whose effects are open, if that layer is still there.
   *
   * Read out of the rows rather than kept beside them: a layer can be deleted while its
   * effects are open, and an editor pointing at a layer that has gone is an editor whose
   * every slider writes to nothing.
   */
  /**
   * A row clicked: alone, or with ⌘/Ctrl one more or fewer, or with Shift the run to it
   * from the last row clicked. A run stays among the clicked row's siblings, as the canvas
   * never picks a layer together with the group it sits in.
   */
  const pick = (id: string, mode: PickMode) => {
    if (mode === 'only' || !onSelectIds) {
      anchor.current = id;
      onSelect(id);
      return;
    }
    const parent = scene.parentOf(id);
    const siblings = rows.filter(row => scene.parentOf(row.id) === parent).map(row => row.id);
    if (mode === 'toggle') anchor.current = id;
    onSelectIds(pickInList(selection, siblings, id, anchor.current, mode));
  };

  const open = showing ? (rows.find((row) => row.id === showing) ?? null) : null;

  /** Whether `id` is `ancestor` or sits somewhere inside it. */
  const within = (id: string, ancestor: string) => {
    if (id === ancestor) return true;
    for (const up of scene.ancestors(id)) if (up === ancestor) return true;
    return false;
  };

  /**
   * Where a dragged row would land if let go at this height, or null for nowhere.
   *
   * layers-F2: the landing row's PARENT was always the new parent, so a container could
   * never be dropped into — the lower half of an artboard's row sent the layer to the top
   * level behind the artboard, under its white page, where it seemed to vanish, and an
   * empty group could never be filled. Now a container's row is a place: its top edge
   * means above it, the rest means into it, on top of what it holds. And a drop the page
   * would refuse — into itself, or an artboard anywhere but the top level — is no drop.
   */
  const landingAt = (x: number, y: number, dragged: string): Landing | null => {
    const moving = scene.get(dragged);
    if (!moving) return null;
    // Only inside the list as it is SEEN: a row scrolled out of view lines up with the
    // pointer's height too, and a drag let go off to the side of the list is a drag given
    // up, not a drop (QA 2026-09-24, layers-F12).
    const seen = list.current?.getBoundingClientRect();
    if (!seen || x < seen.left || x > seen.right || y < seen.top || y > seen.bottom) return null;
    for (const element of list.current?.querySelectorAll<HTMLElement>('[data-row]') ?? []) {
      const box = element.getBoundingClientRect();
      if (y < box.top || y > box.bottom) continue;
      const id = element.dataset.row!;
      const node = scene.get(id);
      if (!node || within(id, dragged)) return null;
      const down = (y - box.top) / box.height;
      const topLevelOnly = TOP_LEVEL_ONLY_TYPES.has(moving.type);
      if (CONTAINER_TYPES.has(node.type) && !topLevelOnly) return { id, where: down < ABOVE_CONTAINER ? 'above' : 'into' };
      const parent = scene.parentOf(id) ?? ROOT_ID;
      if (topLevelOnly && parent !== ROOT_ID) return null;
      return { id, where: down > 0.5 ? 'below' : 'above' };
    }
    return null;
  };

  return (
    <div className={focused ? `focused-layers ${compact ? 'compact-layers ' : ''}flex min-h-0 flex-1 flex-col` : "grid grid-cols-[minmax(0,1fr)] gap-[6px] p-[10px]"}>
      {!focused && <div className="flex h-[26px] items-center px-[4px]">
        <span className="min-w-0 flex-1 text-[15px] font-semibold text-label">Layers</span>
        <span className="tnum text-[12px] text-label-2">{rows.length}</span>
      </div>}
      {rows.length === 0 && <p className={`${compact ? 'px-[8px]' : 'px-[4px]'} pb-[8px] text-[12px] text-label-2`}>{compact ? parentId === ROOT_ID ? 'Place objects outside an artboard to see them here.' : 'Draw or add assets to this artboard.' : 'No layers'}</p>}
      <ul ref={list} className={`ui-scroll flex flex-col gap-[2px] ${focused ? "min-h-0 flex-1" : "max-h-[420px]"}`}>
        {rows.map(({ id, node, depth }) => {
          const chosen = selection.includes(id);
          const open = swiped === id;
          const target = drop?.id === id;
          const hidden = node.visible === false;
          return (
            <li key={id} data-row={id} className="relative shrink-0 overflow-hidden rounded-[10px]" style={{ marginLeft: Math.min(depth, INDENT_LEVELS) * 14 }}>
              {/* The actions sit behind the row, and the row slides off them. */}
              {/* Inert while closed: invisible buttons were still reached by Tab, so Return
                  could delete a layer nobody saw a button for (QA 2026-09-24, layers-F14). */}
              <div
                aria-hidden={!open}
                inert={!open}
                className="absolute inset-y-0 right-0 flex items-stretch overflow-hidden rounded-r-[10px] bg-fill-1 transition-opacity duration-150"
                style={{ width: SWIPE_OPEN, opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none' }}
              >
                {actions.map((action) => (
                  <button
                    key={action.id}
                    type="button"
                    aria-label={action.label}
                    {...tip(action.label)}
                    className={`grid flex-1 place-items-center transition-colors ${action.danger ? 'bg-danger text-white' : 'text-label hover:bg-fill-selected'}`}
                    onClick={(event) => {
                      // Focus leaves the button once it has run, so a Space pressed next
                      // (to pan) does not run it again.
                      event.currentTarget.blur();
                      action.run(id);
                      setSwiped(null);
                    }}
                  >
                    <Icon name={action.icon} size={17} />
                  </button>
                ))}
              </div>

              {target && drop!.where === 'into' ? (
                <span aria-hidden className="pointer-events-none absolute inset-0 z-[2] rounded-[10px] ring-2 ring-inset ring-accent" />
              ) : target ? (
                <span aria-hidden className={`absolute inset-x-[6px] z-[2] h-[2px] rounded-full bg-accent ${drop!.where === 'below' ? 'bottom-0' : 'top-0'}`} />
              ) : null}

              <div
                data-selected={chosen || undefined}
                className={`layer-row group relative flex h-[40px] items-center gap-[8px] rounded-[10px] pl-[6px] pr-[4px] transition-transform duration-200 ${
                  chosen ? (focused ? 'bg-accent-soft text-accent-text' : 'bg-accent text-on-accent') : 'text-label hover:bg-fill-2'
                }`}
                style={{ transform: `translateX(${open ? -SWIPE_OPEN : 0}px)`, opacity: dragging?.id === id ? 0.45 : 1 }}
                onPointerDown={(event) => {
                  if (event.button !== 0 || renaming === id) return;
                  // A control on the row is its own business: it is pressed, not dragged.
                  if ((event.target as HTMLElement).closest('input, select, button[data-control], [data-control]')) return;
                  const start = { x: event.clientX, y: event.clientY };
                  const element = event.currentTarget;
                  let moved: 'none' | 'swipe' | 'drag' = 'none';
                  // Where the pointer got to, kept here rather than in state: the handlers
                  // below are made once, and state they close over stays as it was then.
                  let lastX = start.x;
                  let lastY = start.y;
                  const pointer = event.pointerId;
                  /**
                   * Near the list's top or bottom edge, a drag scrolls the list, a frame at a
                   * time while the hand stays there: the list never scrolled, so a layer could
                   * not be taken past the rows in view (QA 2026-09-24, layers-F12).
                   */
                  let edgeFrame = 0;
                  const EDGE = 28;
                  const nudge = () => {
                    edgeFrame = 0;
                    const box = list.current;
                    if (!box || moved !== 'drag') return;
                    const seen = box.getBoundingClientRect();
                    if (lastX < seen.left || lastX > seen.right) return;
                    const by = lastY < seen.top + EDGE ? -(seen.top + EDGE - lastY) : lastY > seen.bottom - EDGE ? lastY - (seen.bottom - EDGE) : 0;
                    if (by === 0) return;
                    box.scrollTop += Math.sign(by) * Math.min(14, 2 + Math.abs(by) / 3);
                    setDrop(landingAt(lastX, lastY, id));
                    edgeFrame = requestAnimationFrame(nudge);
                  };

                  const move = (e: PointerEvent) => {
                    if (e.pointerId !== pointer) return;
                    const dx = e.clientX - start.x;
                    const dy = e.clientY - start.y;
                    if (moved === 'none') {
                      if (Math.abs(dx) > DRAG_START && Math.abs(dx) > Math.abs(dy)) moved = 'swipe';
                      else if (Math.abs(dy) > DRAG_START) {
                        moved = 'drag';
                        setDragging({ id, y: e.clientY });
                      }
                      // The pointer is only taken once the press turns into a gesture:
                      // capture it sooner and the click never reaches a control.
                      if (moved !== 'none') {
                        try {
                          element.setPointerCapture(e.pointerId);
                        } catch {
                          // The window is listening anyway.
                        }
                      }
                    }
                    if (moved === 'swipe') setSwiped(dx < -DRAG_START ? id : null);
                    if (moved === 'drag') {
                      lastX = e.clientX;
                      lastY = e.clientY;
                      setDragging({ id, y: e.clientY });
                      setDrop(landingAt(e.clientX, e.clientY, id));
                      if (!edgeFrame) edgeFrame = requestAnimationFrame(nudge);
                    }
                  };
                  /**
                   * The gesture always ends: heard on the window, on release, cancel or a lost
                   * capture alike. Listening on the row missed a release over another row, and
                   * a touch scroll's cancel, and the row went on sliding open or dimming under
                   * later hovers (QA 2026-09-24, layers-F15).
                   */
                  const finish = () => {
                    window.removeEventListener('pointermove', move);
                    window.removeEventListener('pointerup', up);
                    window.removeEventListener('pointercancel', cancel);
                    element.removeEventListener('lostpointercapture', cancel);
                    if (edgeFrame) cancelAnimationFrame(edgeFrame);
                    edgeFrame = 0;
                    setDragging(null);
                    setDrop(null);
                  };
                  const cancel = () => finish();
                  const up = (e: PointerEvent) => {
                    if (e.pointerId !== pointer) return;
                    const was = moved;
                    moved = 'none';
                    finish();
                    // A press that went nowhere is a tap, and a tap chooses the layer.
                    if (was === 'none') {
                      if (swiped) setSwiped(null);
                      else {
                        pick(id, pickModeOf(e));
                        // A layer picked from inside a group here, not with Return on the canvas (lib/hints).
                        const parent = scene.parentOf(id);
                        if (parent && scene.get(parent)?.type === 'group') emitHintSignal('layers.pickedInside', { nested: true });
                      }
                    }
                    if (was === 'drag') {
                      const landing = landingAt(lastX, lastY, id);
                      if (landing?.where === 'into') {
                        // No neighbour named: the engine puts it at the front, the top of
                        // the container's stack — the row just under the container's own.
                        onMove(id, { parentId: landing.id });
                      } else if (landing) {
                        const parent = scene.parentOf(landing.id) ?? ROOT_ID;
                        // The list reads top-down and the stack is drawn bottom-up, so
                        // dropping below a row means going behind it: "before" to the engine.
                        onMove(id, landing.where === 'below' ? { parentId: parent, before: landing.id } : { parentId: parent, after: landing.id });
                      }
                    }
                  };
                  window.addEventListener('pointermove', move);
                  window.addEventListener('pointerup', up);
                  window.addEventListener('pointercancel', cancel);
                  element.addEventListener('lostpointercapture', cancel);
                }}
              >
                {focused ? <>
                  {scene.childrenOf(id).length > 0 ? <IconButton data-control className="layer-expand" icon={compact ? expanded.has(id) ? 'nav-chevron' : 'nav-chevron-right' : expanded.has(id) ? 'chevron' : 'chevron-right'} label={`Toggle ${node.name || 'group'}`} aria-expanded={expanded.has(id)} size={compact ? 18 : 20} onClick={() => setExpanded(current => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next; })} /> : compact ? <span className="layer-expand-spacer" aria-hidden /> : null}
                  {/*
                    A drawn layer shows ITSELF: twelve rows reading "path" under twelve pen
                    marks said nothing about which was which (Tarek, 2026-09-30). A kind's
                    mark stays where the mark says more than a picture that small would —
                    text, a group, a picture (whose bytes the list does not have) — and a
                    locked layer keeps its lock.
                  */}
                  {compact ? <span className={`layer-type-icon text-label-2 ${hidden ? 'opacity-45' : ''}`}><Icon name={node.type === 'artboard' ? 'nav-artboard' : CONTAINER_TYPES.has(node.type) ? 'nav-folder' : node.type === 'text' ? 'nav-text' : node.type === 'image' ? 'nav-image' : node.type === 'path' ? 'nav-path' : 'nav-shape'} size={16} /></span> : !node.locked && drawsItself(node) ? (
                    <Thumbnail scene={scene} id={id} version={version} dim={hidden} size={24} />
                  ) : (
                    <span className={`grid size-[24px] shrink-0 place-items-center text-label-2 ${hidden ? 'opacity-45' : ''}`}><Icon name={node.locked ? 'lock' : node.type === 'artboard' ? 'artboard' : CONTAINER_TYPES.has(node.type) ? 'folder' : node.type === 'text' ? 'text' : node.type === 'image' ? 'pictures' : node.type === 'path' ? 'pen' : 'square'} size={16} /></span>
                  )}
                </> : <Thumbnail scene={scene} id={id} version={version} dim={hidden} />}

                {renaming === id ? (
                  <RenameField
                    label="Layer name"
                    value={node.name ?? ''}
                    selectAll
                    keepKeys
                    className="h-[24px] min-w-0 flex-1 rounded-[6px] bg-fill-1 px-[6px] text-[13px] text-label outline-none"
                    onCommit={(name) => onUpdate(id, { name })}
                    onEnd={() => setRenaming(null)}
                  />
                ) : (
                  <span role="button" tabIndex={0} aria-label={node.name || node.type} aria-pressed={compact ? chosen : undefined} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); pick(id, pickModeOf(event)); } }} className={`min-w-0 flex-1 ${hidden ? 'opacity-45' : ''}`} onDoubleClick={() => setRenaming(id)} {...tip(compact ? `${node.name || node.type} · Double-click to rename` : 'Double-click to rename')}>
                    <span className="block truncate text-[13px]">{node.name || node.type}{compact && node.isMask && <span className="ml-1 text-[11px] text-label-2"> · Mask</span>}</span>
                    {!compact && (node.isMask || node.locked) ? (
                      <span className={`block truncate text-[11px] ${chosen && !focused ? 'text-on-accent/75' : 'text-label-2'}`}>
                        {[node.isMask ? 'Mask' : null, node.locked ? 'Locked' : null].filter(Boolean).join(' · ')}
                      </span>
                    ) : null}
                  </span>
                )}

                {/* FX: the one mark that says a layer is not what it appears to be, and the
                    way back in to change what it carries. */}
                {effectsOf(node).length > 0 && (
                  <button
                    type="button"
                    data-control
                    aria-pressed={showing === id}
                    {...tip(effectsOf(node).map(effect => effect.type === 'shader' ? getShader?.(effect.shaderId)?.name ?? 'Shader' : effectName(effect)).join(', '))}
                    aria-label={`Effects on ${node.name || node.type}`}
                    aria-haspopup="dialog"
                    aria-expanded={showing === id}
                    className={`inline-flex h-[20px] shrink-0 cursor-pointer items-center justify-center rounded-full px-[7px] text-[10px] font-semibold leading-none ${
                      chosen && !focused ? 'bg-white/25 text-on-accent' : showing === id ? 'bg-accent text-on-accent' : 'bg-accent-soft text-accent-text'
                    }`}
                    onClick={event => { setEffectsAnchor(event.currentTarget); setShowing(open => open === id ? null : id); }}
                  >
                    FX
                  </button>
                )}

                {!focused && <button
                  type="button"
                  data-control
                  ref={blending === id ? setBlendAnchor : undefined}
                  aria-label={`Blend and opacity: ${blendName(node)}, ${Math.round((node.opacity ?? 1) * 100)}%`}
                  {...tip(`${BLEND_LABELS[blendName(node)] ?? blendName(node)} · ${Math.round((node.opacity ?? 1) * 100)}%`)}
                  className={`grid h-[22px] min-w-[22px] shrink-0 place-items-center rounded-[6px] px-[5px] text-[11px] font-semibold ${
                    chosen ? 'bg-white/20 text-on-accent' : 'bg-fill-1 text-label-2 hover:text-label'
                  }`}
                  onClick={(event) => {
                    setBlendAnchor(event.currentTarget);
                    setBlending((open) => (open === id ? null : id));
                  }}
                >
                  {blendLetter(node)}
                </button>}

                <span data-control className={focused ? `layer-row-actions flex ${chosen || compact && (hidden || node.locked) ? '' : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 pointer-coarse:opacity-100'}` : chosen ? 'text-on-accent' : ''}>
                  <IconButton
                    data-control
                    icon={compact ? hidden ? 'nav-eye-off' : 'nav-eye' : hidden ? 'eye-off' : 'eye'}
                    label={compact ? `${hidden ? 'Show' : 'Hide'} ${node.name || node.type}` : hidden ? 'Show' : 'Hide'}
                    size={compact ? 22 : 26}
                    tone={chosen ? 'default' : 'muted'}
                    className={chosen && !focused ? '!text-on-accent hover:!bg-white/15' : ''}
                    onClick={() => onUpdate(id, { visible: hidden })}
                  />
                  {compact && actions.some(action => action.id === 'lock') && <IconButton data-control icon={node.locked ? 'nav-lock' : 'nav-unlock'} label={`${node.locked ? 'Unlock' : 'Lock'} ${node.name || node.type}`} size={22} onClick={() => actions.find(action => action.id === 'lock')?.run(id)} />}
                  {focused && <IconButton data-control icon="more" label={`Actions for ${node.name || node.type}`} size={compact ? 22 : 26} onClick={(event) => { setBlending(null); setRowMenu({ id, anchor: event.currentTarget }); }} />}
                </span>
              </div>
            </li>
          );
        })}
      </ul>

      {rowMenu && (() => {
        const node = scene.get(rowMenu.id); if (!node) return null;
        const up = layerMoveTarget(scene, rowMenu.id, -1), down = layerMoveTarget(scene, rowMenu.id, 1);
        const items: MenuEntry[] = [
          ...(onEditText && node.type === 'text' && !scene.lockedHere(rowMenu.id) ? [{ id: 'edit-text', label: 'Edit text', onSelect: () => onEditText(rowMenu.id) }] : []),
          { id: 'rename', label: 'Rename…', onSelect: () => setRenaming(rowMenu.id) },
          ...(onGroup && selection.length > 1 && selection.includes(rowMenu.id) ? [{ id: 'group', label: 'Group', onSelect: () => onGroup(rowMenu.id) }] : []),
          ...(onUngroup && node.type === 'group' && !node.locked ? [{ id: 'ungroup', label: 'Ungroup', onSelect: () => onUngroup(rowMenu.id) }] : []),
          ...actions.map(action => ({ id: action.id, label: action.id === 'lock' && node.locked ? 'Unlock' : action.label, danger: action.danger, onSelect: () => action.run(rowMenu.id) })),
          { kind: 'separator' },
          { id: 'up', label: 'Move up', disabled: !up || !!node.locked, onSelect: () => { if (up) onMove(rowMenu.id, up); } },
          { id: 'down', label: 'Move down', disabled: !down || !!node.locked, onSelect: () => { if (down) onMove(rowMenu.id, down); } },
          { kind: 'submenu', id: 'move-to', label: 'Move to artboard', disabled: !!node.locked, items: [
            ...scene.childrenOf(ROOT_ID).filter(one => scene.get(one)?.type === 'artboard').map(one => ({ id: one, label: scene.get(one)?.name || 'Artboard', disabled: scene.parentOf(rowMenu.id) === one, onSelect: () => onMove(rowMenu.id, { parentId: one }) })),
            { id: 'canvas', label: 'Canvas', disabled: scene.parentOf(rowMenu.id) === ROOT_ID, onSelect: () => onMove(rowMenu.id, { parentId: ROOT_ID }) },
          ] },
          { kind: 'separator' },
          { id: 'blend' , label: 'Blend and opacity…', onSelect: () => { setBlendAnchor(rowMenu.anchor); setBlending(rowMenu.id); } },
          ...(effectsOf(node).length ? [{ id: 'effects', label: 'Effects…', onSelect: () => { setEffectsAnchor(rowMenu.anchor); setShowing(rowMenu.id); } }] : []),
          ...(onAddToChat ? [{ kind: 'separator' as const }, { id: 'add-to-chat', label: 'Add to chat', onSelect: () => onAddToChat(rowMenu.id) }] : []),
        ];
        return <Menu open anchor={rowMenu.anchor} onClose={() => setRowMenu(null)} label="Layer actions" items={items} />;
      })()}

      {/* Blend mode, opacity and masking, behind the letter. */}
      {blending ? (
        <Popover open anchor={blendAnchor} onClose={() => setBlending(null)} placement={focused ? "right" : "left"} offset={focused ? 24 : 8} align="start" width={240} label="Blend">
          {(() => {
            const row = rows.find((one) => one.id === blending);
            return row ? <Blending node={row.node} loose={(scene.parentOf(row.id) ?? ROOT_ID) === ROOT_ID} onChange={(props) => onUpdate(row.id, props)} /> : null;
          })()}
        </Popover>
      ) : null}

      {open && (
        <Popover open anchor={effectsAnchor ?? list.current} positionAnchor={list.current?.closest<HTMLElement>('aside') ?? list.current}
          placement={focused ? 'right' : 'left'} align="start" offset={12} width={304}
          label={`Effects on ${open.node.name || open.node.type}`} onClose={() => setShowing(null)} ownsKeyboard
          keepOpenWithin=".focused-layers" className="layer-effects-popover effects-stack-popover">
          <header className="effects-stack-heading">
            <div className="min-w-0 flex-1"><h2 className="text-[13px] font-semibold">Effects</h2><p className="truncate text-[11px] text-label-2">{open.node.name || open.node.type}</p></div>
            <IconButton icon="close" label="Close Effects" size={24} onClick={() => setShowing(null)} />
          </header>
          <div className="effects-stack-body ui-scroll">
            <EffectsList
              key={open.id}
              effects={effectsOf(open.node)}
              units={units}
              getShader={getShader}
              collapsible
              onChange={(index, props) => onEffect(open.id, listedEffects(open.node).at[index]!, props)}
              onRemove={(index) => onRemoveEffect(open.id, listedEffects(open.node).at[index]!)}
              onReorder={(index, to) => onMoveEffect(open.id, listedEffects(open.node).at[index]!, listedEffects(open.node).at[to]!)}
            />
            {effectsOf(open.node).length === 0 && <p className="text-[12px] text-label-2">No effects on this layer.</p>}
          </div>
        </Popover>
      )}
    </div>
  );
}

/** The longest name the page will keep (the schema's cap on a node's name). */
const NAME_MOST = 256;

/**
 * A name being typed in a list row: the layers list's and the artboards list's.
 *
 * Kept when the field is left by any road — Return, a click elsewhere, or the whole panel
 * closing. A click on the canvas closes the panel before focus moves, and the field was
 * gone before its blur could run, so the new name was lost (QA 2026-09-24, layers-F10).
 * Escape throws the name away, wherever it is heard: the panel takes Escape before the
 * field does, to close itself.
 *
 * Capped at the page's own limit, which refused a longer name without a word
 * (QA 2026-09-24, layers-F18).
 */
export function RenameField({
  value,
  label,
  className,
  selectAll = false,
  keepKeys = false,
  onCommit,
  onEnd,
}: {
  value: string;
  label: string;
  className: string;
  /** Start with the whole name chosen, so typing replaces it. */
  selectAll?: boolean;
  /** Keep every key from reaching the page behind. */
  keepKeys?: boolean;
  onCommit: (name: string) => void;
  /** The field is finished with, kept or not. */
  onEnd: () => void;
}) {
  const text = useRef(value);
  const done = useRef(false);
  const commit = useRef(() => {});
  commit.current = () => {
    if (done.current) return;
    done.current = true;
    const name = text.current.trim().slice(0, NAME_MOST);
    if (name && name !== value) onCommit(name);
  };
  useEffect(() => {
    // Set again on every mount: the development double mount runs the cleanup once first.
    done.current = false;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') done.current = true;
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      // Leaving without a blur — the panel closed under it — still keeps what was typed.
      commit.current();
    };
  }, []);
  return (
    <input
      autoFocus
      aria-label={label}
      defaultValue={value}
      maxLength={NAME_MOST}
      className={className}
      onFocus={selectAll ? (event) => event.currentTarget.select() : undefined}
      onChange={(event) => {
        text.current = event.currentTarget.value;
      }}
      onBlur={() => {
        commit.current();
        onEnd();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
        if (event.key === 'Escape') {
          done.current = true;
          onEnd();
        }
        if (keepKeys) event.stopPropagation();
      }}
    />
  );
}

/** What a layer is carrying, read the same way wherever it is asked for — Adjust (the Edit tab's) left out. */
function effectsOf(node: NodeRecord): readonly Effect[] {
  return listedEffects(node).effects;
}

/** A path or a shape: a layer whose own small picture tells it from the next one. */
function drawsItself(node: NodeRecord): boolean {
  return !CONTAINER_TYPES.has(node.type) && node.type !== 'text' && node.type !== 'image' && node.type !== 'slice' && node.type !== 'effector';
}

/** The layer itself, drawn small: the engine's own SVG, over a checkerboard. */
export function Thumbnail({ scene, id, version, dim, size = 32, board = false }: { scene: Scene; id: string; version: number; dim?: boolean; size?: number; board?: boolean }) {
  // A layer with nothing under it is redrawn when IT changes — the scene hands out a new
  // record then — not on every change to the page: a list of a hundred paths otherwise
  // wrote a hundred SVGs for each step of a drag. Anything holding other layers still
  // follows the page, since what it shows is theirs.
  const stamp = !board && scene.childrenOf(id).length === 0 ? scene.get(id) : version;
  const source = useMemo(() => {
    try {
      // A whole artboard draws as the page it is, background and all; a layer draws alone.
      const svg = board ? toSvg(scene, { artboardId: id, indent: false }) : toSvg(scene, { nodeId: id, padding: 1, indent: false });
      return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    } catch {
      // A layer the writer cannot draw is still a layer; the row does without a picture.
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene, id, stamp, board]);

  return (
    <span
      className={`grid shrink-0 place-items-center overflow-hidden ${size < 28 ? 'rounded-[5px]' : 'rounded-[7px]'} ${dim ? 'opacity-45' : ''}`}
      style={{ width: size, height: size, background: 'repeating-conic-gradient(#d4d4d8 0% 25%, #ffffff 0% 50%) 50% / 8px 8px', boxShadow: 'inset 0 0 0 0.5px rgb(0 0 0 / 0.2)' }}
    >
      {source && <img src={source} alt="" style={{ maxWidth: size - 4, maxHeight: size - 4 }} draggable={false} />}
    </span>
  );
}

const BLEND_LABELS: Record<string, string> = {
  'pass-through': 'Pass Through',
  normal: 'Normal',
  multiply: 'Multiply',
  screen: 'Screen',
  overlay: 'Overlay',
  darken: 'Darken',
  lighten: 'Lighten',
  'color-dodge': 'Color Dodge',
  'color-burn': 'Color Burn',
  'hard-light': 'Hard Light',
  'soft-light': 'Soft Light',
  difference: 'Difference',
  exclusion: 'Exclusion',
  hue: 'Hue',
  saturation: 'Saturation',
  color: 'Color',
  luminosity: 'Luminosity',
};

/**
 * Blend mode, opacity and masking: what Procreate keeps behind the letter.
 *
 * `loose` is a layer on the page itself, in no group or artboard. A mask clips the layers
 * above it inside the thing that holds them, and the page is never drawn as one — so a
 * loose mask read On and masked nothing, in the editor and in every export (layers-N6).
 * It cannot be turned on there; one already on can still be turned off.
 */
function Blending({ node, loose, onChange }: { node: NodeRecord; loose: boolean; onChange: (props: Record<string, unknown>) => void }) {
  const cannotMask = loose && node.isMask !== true;
  // Pass Through for a Cloner and a Blend too, which start out that way: the menu read
  // Normal for them and could not choose it again (QA 2026-09-24, cloner-F22).
  const passes = node.type === 'group' || node.type === 'blend' || node.type === 'cloner';
  const modes: LayerBlendMode[] = passes ? ['pass-through', ...BLEND_MODES] : [...BLEND_MODES];
  const opacity = Math.round((node.opacity ?? 1) * 100);
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-[10px] p-[14px]">
      <div className="grid grid-cols-[minmax(0,1fr)] gap-[2px]">
        <div className="flex items-center gap-[8px]">
          <span className="min-w-0 flex-1 text-[12px] text-label-2">Opacity</span>
          <NumberField className="w-[64px]" label="Opacity" value={opacity} min={0} max={100} decimals={0} suffix="%" onChange={(value) => onChange({ opacity: value / 100 })} />
        </div>
        <Slider label="Opacity" value={opacity} min={0} max={100} onChange={(value) => onChange({ opacity: value / 100 })} />
      </div>
      <div className="flex items-center gap-[8px]">
        <span className="w-[52px] shrink-0 text-[12px] text-label-2">Blend</span>
        <PopUpButton
          className="min-w-0 flex-1"
          label="Blend mode"
          value={(node as { blendMode?: LayerBlendMode }).blendMode ?? 'normal'}
          onChange={(blendMode) => onChange({ blendMode })}
          options={modes.map((mode) => [mode, BLEND_LABELS[mode] ?? mode] as const)}
        />
      </div>
      <div className="flex items-center gap-[8px]">
        <span className="min-w-0 flex-1 text-[12px] text-label">Use as Mask</span>
        <Switch label="Use as mask" checked={node.isMask === true} disabled={cannotMask} onChange={(isMask) => onChange({ isMask })} />
      </div>
      {/* Said plainly, because which way a mask works is the thing people get wrong — and
          why the switch is off, when it is, since a dimmed control with no reason is a
          control that looks broken. */}
      <Hint>{cannotMask ? 'A mask works inside a group or an artboard. Group this layer with the ones above it first.' : 'A mask shows the layers above it only where this layer is painted.'}</Hint>
    </div>
  );
}

const blendName = (node: NodeRecord) => (node as { blendMode?: string }).blendMode ?? 'normal';

/** Procreate's shorthand: one letter for the mode, N for a layer that just sits there. */
function blendLetter(node: NodeRecord): string {
  const mode = blendName(node);
  if (mode === 'normal') return 'N';
  if (mode === 'pass-through') return 'P';
  return mode[0]!.toUpperCase();
}
